import {
	ContextMenu,
	ContextMenuDeleteItem,
	IDataListColumn,
	Id,
	PaginatedAutoDataList,
	isSuccess,
	laravelPaginatedListFetchConfig,
} from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { createUserApplicationService } from "../shared/api/applicationService";
import { ApplicationIndexResource } from "../shared/types";

export const UserApplicationList: FC = () => {
	const { t } = useTranslation();
	const { userId = "" } = useParams();
	const queryClient = useQueryClient();
	const userApplicationService = useMemo(() => createUserApplicationService(userId), [userId]);
	const detachApplications = useCallback(
		async (applicationId: Id) => {
			const response = await userApplicationService.detachApplications(applicationId);

			if (isSuccess(response)) {
				void queryClient.invalidateQueries({ queryKey: userApplicationService.listKey });
			}
		},
		[queryClient, userApplicationService],
	);

	const columns = useMemo<IDataListColumn<ApplicationIndexResource>[]>(
		() => [
			{ type: "value", heading: t("Name"), valueKey: "subdomain" },
			{ type: "value", heading: t("Type"), valueKey: "type" },
			{
				size: "max-content",
				type: "actions",
				heading: t("Actions"),
				action: ({ id }) => (
					<ContextMenu hideIconOnOnlyShortcuts={true} displayShortcutsAs="icon">
						<ContextMenuDeleteItem
							action={() => detachApplications(id)}
							label={t("Detach application")}
							heading={t("Detach application")}
							labels={{ close: t("Close"), confirm: t("Confirm") }}
							title={t("Detach application")}
							shortcut={true}
						>
							{t(`Are you sure you want to remove this application from the user?`)}
						</ContextMenuDeleteItem>
					</ContextMenu>
				),
			},
		],
		[detachApplications, t],
	);

	return (
		<PaginatedAutoDataList
			name="userApplications"
			heading={t("User applications")}
			empty={t("This user does not have any application assigned.")}
			columns={columns}
			listFetchConfig={laravelPaginatedListFetchConfig(userApplicationService.listKey, userApplicationService.index)}
		/>
	);
};
